import { useCallback, useMemo } from 'react';

import { PeerTypes, useShowCampaignById_new } from '@givebacks/api/store';
import { BenefitsListModal } from '@givebacks/shop';
import { useAtomValue } from 'jotai';
import { Modal, ScrollArea, Title, createModalsFactory } from 'ui';
 
import { cartIdAtom } from '../../../Context/store/store.provider';
import { CampaignDonateOptionsModal } from '../../../crowdfunding/components/Modals/CampaignDonateSelect/CampaignDonateOptionsModal';
import { PeerCreditModal } from '../../../crowdfunding/components/Modals/PeerCreditModal';
import { CampaignSelection } from './CampaignSelectionModal/CampaignSelectionModal';
import { StoreItemOptionsModal } from './StoreItemOptionsModal';

interface SelectStoreItemModalFlowProps {
  storeId?: string;
  storeItemId?: string;
  children?: React.ReactNode | React.ReactNode[];
  isAddToCartAvailable?: boolean;

  /** Modal in which you wish to pass as a child to the provider */
  childModal?: (arg0: StoreItemModalProps) => React.ReactNode | React.ReactNode[];

  /** Call back when the modal states goes from active to empty */
  onClose?: () => void;
}
type SelectStoreItemModalIds =
  | 'ListStoreItems'
  | 'StoreItemCrediting'
  | 'StoreItemOptions'
  | 'Child'
  | 'StoreItemDonateOptions'
  | 'StoreItemBenefitList';

type PendingCartFields = {
  credit_to: string[] | undefined;
  credit_write_in: string[] | undefined;
  peerType?: PeerTypes | null;
};

type SelectStoreItemModalFlowMeta = {
  storeId?: string;
  storeItemId?: string;
  cartCredit?: PendingCartFields;
  isAddToCartAvailable?: boolean;
  isBenefitsEnabled?: boolean;
};

const {
  Provider,
  useProvider: useSelectStoreItemProvider,
  ModalTitleWithBackButton,
} = createModalsFactory<SelectStoreItemModalIds[], SelectStoreItemModalFlowMeta>([
  'Child',
  'ListStoreItems',
  'StoreItemCrediting',
  'StoreItemOptions',
  'StoreItemDonateOptions',
  'StoreItemBenefitList',
]);

const CampaignSelectionModal = () => {
  const { opened, close, openModalById, setMeta, meta } = useSelectStoreItemProvider({
    id: 'ListStoreItems',
  });

  const resetCartCredit = useCallback(() => {
    setMeta((prev) => ({ ...prev, cartCredit: undefined }));
  }, [setMeta]);

  // On Item Selection or Peer Credit
  // Set the storeId and storeItemId
  const handleSetIds = useCallback(
    ({ storeId, storeItemId }: SelectStoreItemModalFlowMeta) => {
      setMeta((prev) => ({ ...prev, storeId, storeItemId }));
    },
    [setMeta]
  );

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={`How Do You Want Your Donation Used?`}
      padding="xl"
      scrollAreaComponent={ScrollArea.Autosize}
      pos="relative"
      size="lg"
    >
      <CampaignSelection
        storeId={meta.storeId}
        onPeerCreditOpen={({ storeId, storeItemId }) => {
          handleSetIds({ storeId, storeItemId });
          resetCartCredit();
          openModalById('StoreItemCrediting');
        }}
        onItemSelect={({ storeId, storeItemId }) => {
          handleSetIds({ storeId, storeItemId });
          resetCartCredit();
          openModalById('StoreItemOptions');
        }}
      />
    </Modal>
  );
};

const CreditModal = () => {
  const { opened, close, openNextStep, setMeta, meta, history, props } = useSelectStoreItemProvider(
    {
      id: 'StoreItemCrediting',
    }
  );

  const handleSetCartCredit = useCallback(
    (cartCredit: SelectStoreItemModalFlowMeta['cartCredit']) => {
      setMeta((prev) => ({ ...prev, cartCredit }));
      openNextStep();
    },
    [openNextStep, setMeta]
  );

  const title = useMemo(
    () =>
      history.history[history.current - 1] === 'ListStoreItems' ? (
        <ModalTitleWithBackButton>
          <Title order={2}>Donate</Title>
        </ModalTitleWithBackButton>
      ) : (
        'Donate'
      ),
    [history]
  );
  return (
    <PeerCreditModal
      onStart={handleSetCartCredit}
      opened={opened}
      onClose={close}
      storeId={meta?.storeId}
      storeItemId={meta?.storeItemId}
      title={title}
      {...props}
    />
  );
};

const StoreItemModal = () => {
  const { opened, meta, props, close } = useSelectStoreItemProvider({ id: 'StoreItemOptions' });
  const cartId = useAtomValue(cartIdAtom);

  return (
    <StoreItemOptionsModal
      onClose={close}
      opened={opened}
      causeId={meta.storeId}
      storeItemId={meta.storeItemId}
      cartId={cartId}
      title={
        meta.isBenefitsEnabled ? (
          <ModalTitleWithBackButton>
            <Title order={2}>Donate</Title>
          </ModalTitleWithBackButton>
        ) : null
      }
      onBuyNow={close}
      pendingCartFields={meta.cartCredit}
      isAddToCartAvailable={meta.isAddToCartAvailable}
      {...props}
    />
  );
};

const CampaignDonateSelectionModal = () => {
  const { opened, close, meta } = useSelectStoreItemProvider({
    id: 'StoreItemDonateOptions',
  });

  return (
    <CampaignDonateOptionsModal
      opened={opened}
      onClose={close}
      title={'Donate'}
      storeId={meta.storeId}
      storeItemId={meta.storeItemId}
    />
  );
};

const CampaignBenefitModal = () => {
  const { opened, close, meta } = useSelectStoreItemProvider({
    id: 'StoreItemBenefitList',
  });

  const { data: campaign } = useShowCampaignById_new({
    campaignId: meta.storeItemId,
    causeId: meta.storeId,
  });

  return (
    <BenefitsListModal
      opened={opened}
      onClose={close}
      campaignId={meta.storeItemId}
      causeId={meta.storeId}
      header={
        <ModalTitleWithBackButton>
          {campaign?.benefit_options?.title ?? 'Benefits'}
        </ModalTitleWithBackButton>
      }
    />
  );
};

interface StoreItemModalProps {
  opened: boolean;
  onClose: () => void;
  openModalById: (id: SelectStoreItemModalIds) => void;
  meta?: ReturnType<typeof useSelectStoreItemProvider>['meta'];
  setMeta?: ReturnType<typeof useSelectStoreItemProvider>['setMeta'];
  currentModalId?: ReturnType<typeof useSelectStoreItemProvider>['currentModalId'];
}

/**
 *
 * Provides a child function with access to the store item modal flow and meta data
 */
const StoreItemModalWrapper = ({
  modal,
}: {
  modal?: (props: StoreItemModalProps) => React.ReactNode | React.ReactNode[];
}) => {
  const { opened, close, openModalById, meta, setMeta, currentModalId, ...modalProps } =
    useSelectStoreItemProvider({
      id: 'Child',
    });

  return modal?.({
    opened,
    onClose: close,
    openModalById,
    meta,
    setMeta,
    currentModalId,
    ...modalProps,
  });
};

const SelectStoreItemModalFlow = ({
  storeId,
  storeItemId,
  children,
  childModal,
  onClose,
  isAddToCartAvailable = true,
}: SelectStoreItemModalFlowProps) => {
  return (
    <Provider
      meta={{
        storeId,
        storeItemId,
        isAddToCartAvailable,
      }}
      onClose={onClose}
    >
      <CampaignSelectionModal />
      <CreditModal />
      <StoreItemModal />
      <CampaignDonateSelectionModal />
      <CampaignBenefitModal />

      {childModal ? <StoreItemModalWrapper modal={childModal} /> : null}
      {children}
    </Provider>
  );
};

export { SelectStoreItemModalFlow, useSelectStoreItemProvider };

export type { SelectStoreItemModalIds };
